// simulator todo - this component was not upgraded by yoav

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";

import { Converter } from "showdown";
import { Message, ToolInvocation } from "ai";
import { toast } from "sonner";
import { PiTranslate } from "react-icons/pi";
import { Loading, Textarea } from "@geist-ui/core"; // simulator todo - replace
import { Pencil1Icon } from "@radix-ui/react-icons";
import { Button, Tooltip, Switch } from "@radix-ui/themes";
import { posthogTrackEvent, SimulatorEvents } from "@/assets/posthog";

import { translateText } from "@/services/translation";

import styles from "./styles.module.scss";
import { MessageSources } from "./messageSources";
import { AgentWrapper, ToolViewer } from "@/v2/components/tool";
import ChainOfThoughts from "../chainOfThoughts/ChainOfThoughts";
import { ChainOfThoughtStep } from "../chainOfThoughts/ChainOfThoughts.types";
import { getVisibleSteps } from "../chainOfThoughts/ChainOfThoughts.rerender";

interface IMessage {
  role: "user" | "assistant";
  content: string;
}

enum Tools {
  SKIP_IT = "skip_it",
  TALK_TO_AN_AGENT = "talk_to_an_agent",
  RESOLVED = "ticket_solved",
  DONT_KNOW = "dont_know",
}

export const Messages = ({
  messages,
  isLoading,
  handleEditMessage,
  messagesContextExport,
  trackingData,
  isLoadingChat,
  isRedrafting = false,
  isGeneratingTopic = false,
  isProcessingMessage = false,
  selectedTopic = null,
}: {
  messages: Message[];
  handleEditMessage: (message: string) => void;
  isLoading: boolean;
  messagesContextExport: { [key: string]: any };
  trackingData: { [key: string]: any };
  isLoadingChat: boolean;
  isRedrafting?: boolean;
  isGeneratingTopic?: boolean;
  isProcessingMessage?: boolean;
  selectedTopic?: any;
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loading = useMemo(
    () =>
      isLoading || isLoadingChat || isProcessingMessage || isGeneratingTopic,
    [isLoading, isLoadingChat, isProcessingMessage, isGeneratingTopic]
  );

  const relevantMessages = useMemo(() => {
    if (!isRedrafting) {
      return messages;
    }
    return messages && Array.isArray(messages) && messages.length > 0
      ? messages.slice(0, -1)
      : [];
  }, [messages, isRedrafting]);

  const messagesWithMock = useMemo(() => {
    if (loading && relevantMessages.length > 0) {
      const lastMessage = relevantMessages[relevantMessages.length - 1];
      if (lastMessage.role === "user") {
        const mockAssistantMessage = {
          id: "mock-assistant-loading",
          role: "assistant" as const,
          content: "",
          annotations: [],
          toolInvocations: [],
        };
        return [...relevantMessages, mockAssistantMessage];
      }
    }
    return relevantMessages;
  }, [loading, relevantMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [messages]);

  return (
    <>
      <>
        {messagesWithMock.map((m, i) => {
          const chainOfThoughtSteps =
            (m.annotations?.[0] as any)?.chainOfThoughtSteps || [];

          const toolSteps = (m.toolInvocations || []).flatMap((tool: any) => {
            const steps = [];

            if (tool.toolName && tool.args) {
              steps.push({
                stepKey: "tool-invocation",
                toolName: tool.toolName,
                toolArgs: tool.args,
                toolId: tool.toolCallId || tool.id || `tool-${Date.now()}`,
              });
            }

            if (tool.state === "result" && tool.result !== undefined) {
              steps.push({
                stepKey: "tool-response",
                toolName: tool.toolName,
                toolId: tool.toolCallId || tool.id || `tool-${Date.now()}`,
                result: tool.result,
                state: tool.state,
              });
            }

            return steps;
          });

          const allSteps = [...chainOfThoughtSteps, ...toolSteps];

          const isLastMessage = i + 1 === messagesWithMock.length;

          return (
            <MessageComponent
              role={m.role === "user" ? "user" : "assistant"}
              content={m.content}
              tools={m.toolInvocations}
              isLast={isLastMessage}
              handleEditMessage={handleEditMessage}
              sources={m.role === "user" ? null : messagesContextExport[m.id]}
              trackingData={trackingData}
              chainOfThoughtSteps={allSteps}
              selectedTopic={selectedTopic}
              isLoading={isLastMessage && loading}
            />
          );
        })}

        {loading && relevantMessages.length === 0 && (
          <MessageComponent
            role="assistant"
            content=""
            isLast={true}
            handleEditMessage={handleEditMessage}
            sources={null}
            trackingData={trackingData}
            selectedTopic={selectedTopic}
            isLoading={loading}
            isGeneratingTopic={isGeneratingTopic}
          />
        )}

        <div ref={messagesEndRef} />
      </>
    </>
  );
};

const convertor = new Converter({
  simplifiedAutoLink: true,
  excludeTrailingPunctuationFromURLs: true,
  openLinksInNewWindow: true,
  images: true,
  smoothLivePreview: true,
  disableForced4SpacesIndentedSublists: true,
});

// Helper to extract iframe URL and its position if present in the template
function extractIframeUrlAndParts(text: string): {
  url: string | null;
  before: string;
  after: string;
} {
  // Matches: iframe:https://something or iframe:http://something
  const match = text.match(/([\s\S]*?)iframe:(https?:\/\/[^\s]+)([\s\S]*)/);
  if (match) {
    return {
      url: match[2],
      before: match[1] || "",
      after: match[3] || "",
    };
  }
  return { url: null, before: "", after: "" };
}

// Add dir="auto" to every block-level element so each paragraph picks its own
// direction from its first strong character (Hebrew → RTL, English → LTR).
// Without this, the whole bubble inherits direction from the very first strong
// char anywhere in the content, which gets fooled by leading English words,
// numbers, or punctuation in an otherwise-Hebrew message.
const addAutoDirToBlocks = (html: string) =>
  html.replace(
    /<(p|li|h[1-6]|blockquote|pre|td|th)(\s|>)/g,
    '<$1 dir="auto"$2'
  );

// [image](https://quack-production-knowledge-management-media.s3.us-east-1.amazonaws.com/quack-plain/snippets/0ec21043-37e4-4db6-a718-1cda979bc4d5/f2aa3f4f-be03-42e8-ac00-98a8a85f0ab5.png)
// Modified markdown-to-HTML converter that handles iframe breaks
const convertMarkdownToHTML = (markdown: string) => {
  if (!markdown) return "";

  // Pre-process images: [image]:(url) -> <img src="url" ... />
  const processedMarkdown = markdown
    .replaceAll("[image]", "![image]");

  // Check for iframe pattern
  const {
    url: iframeUrl,
    before: iframeBefore,
    after: iframeAfter,
  } = extractIframeUrlAndParts(processedMarkdown);

  if (iframeUrl) {
    // Remove code block markers if present
    const before = iframeBefore.replace(/```/g, "");
    const after = iframeAfter.replace(/```/g, "");
    // Compose HTML with iframe in the middle
    return `
      <div>
        ${before ? addAutoDirToBlocks(convertor.makeHtml(before)) : ""}
        <iframe
          src="${iframeUrl}"
          title="Embedded content"
          style="width:100%;min-height:400px;border:1px solid #ccc;border-radius:8px;"
          allow="fullscreen"
        ></iframe>
        ${after ? addAutoDirToBlocks(convertor.makeHtml(after)) : ""}
      </div>
    `;
  }

  // No iframe, just convert as usual
  // Custom post-processing to keep punctuation immediately after a link inside the same <p>
  let html = convertor.makeHtml(processedMarkdown);

  // Regex: move leading punctuation (.,!?:;) that is between adjacent </a> and <p> to just before </a>
  html = html.replace(/(<a\b[^>]*>[^<]+<\/a>)([.,!?:;]+)(<\/p>)/g, "$1$2$3");

  return addAutoDirToBlocks(html);
};

export const MessageComponent = ({
  role,
  content,
  tools,
  handleEditMessage,
  isLoading = false,
  isLast = false,
  sources,
  trackingData,
  editDisabled = false,
  chainOfThoughtSteps,
  selectedTopic = null,
  isGeneratingTopic = false,
}: IMessage & {
  isLoading?: boolean;
  handleEditMessage: (m: string) => void;
  tools?: ToolInvocation[];
  isLast?: boolean;
  sources: null | any[];
  trackingData: { [key: string]: any };
  editDisabled?: boolean;
  chainOfThoughtSteps?: ChainOfThoughtStep[];
  selectedTopic?: any;
  isGeneratingTopic?: boolean;
}) => {
  const [isHover, setHover] = useState(false);
  const [value, setValue] = useState(content);
  const [isEdit, setEdit] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<string | null>(
    null
  );
  const Wrapper = role === "assistant" ? AgentWrapper : Fragment;

  useEffect(() => {
    setValue(content);
  }, [content]);

  const isSkip = tools?.some(
    (tool) => tool.state === "result" && tool.result === Tools.SKIP_IT
  );

  const isEscalated = tools?.some(
    (tool) => tool.state === "result" && tool.result === Tools.TALK_TO_AN_AGENT
  );

  const escalationMessage = tools?.find(
    (tool) => tool.toolName === "escalate_with_message"
  )?.args?.message as string | undefined;

  const isSimpleEscalation = isEscalated && !escalationMessage;

  const isDontKnow = tools?.some(
    (tool) => tool.state === "result" && tool.result === Tools.DONT_KNOW
  );

  const isResolved = tools?.some(
    (tool) => tool.state === "result" && tool.result === Tools.RESOLVED
  );

  const isEscalateWithMessage = tools?.some(
    (tool) =>
      tool.state === "result" && tool.toolName === "escalate_with_message"
  );

  const props =
    role === "assistant"
      ? {
        hideIcon:
          isSkip ||
          isSimpleEscalation ||
          !(content || escalationMessage) ||
          isDontKnow,
      }
      : {};

  const displayContent = translatedContent || escalationMessage || content;

  // Hebrew (U+0590–05FF) and Arabic (U+0600–06FF + presentation forms) ranges.
  // Explicit detection beats `dir="auto"`, which gets fooled when a Hebrew
  // message starts with English/digits/punctuation.
  const isRtlContent = useMemo(
    () => new RegExp("[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]").test(displayContent || ""),
    [displayContent]
  );

  return (
    <>
      {role === "assistant" &&
        (isLoading ||
          isGeneratingTopic ||
          getVisibleSteps(chainOfThoughtSteps || []).length > 0) && (
          <ChainOfThoughts
            title="Chain of thought"
            steps={chainOfThoughtSteps || []}
            defaultOpen={false}
            className={styles.chainOfThoughts}
            currentStepIndex={-1}
            sources={sources || undefined}
            isLoading={isLoading || isGeneratingTopic}
            hasTopic={!!selectedTopic}
            isLastMessage={isLast}
          />
        )}

      {tools?.map((tool) => {
        if (
          tool.state === "result" &&
          (tool.result === Tools.TALK_TO_AN_AGENT ||
            tool.result === Tools.SKIP_IT ||
            tool.result === Tools.DONT_KNOW) &&
          tool.toolName !== "escalate_with_message"
        ) {
          return (
            <Wrapper {...props} isEscalation hideIcon>
              <ToolViewer tool={tool} isEscalation />
            </Wrapper>
          );
        }

        if (tool.state === "result" && tool.result === Tools.RESOLVED) {
          return (
            <Wrapper {...props} hideIcon>
              <ToolViewer tool={tool} isResolved />
            </Wrapper>
          );
        }

        return null;
      })}

      {isSkip || isSimpleEscalation || isDontKnow || isResolved ? null : (
        <Wrapper {...props}>
          <>
            {(!isLoading || content) && (
              <div
                className={cx(styles.message, styles[role], {
                  [styles.loading]: isLoading,
                  [styles.systemMessage]:
                    isSkip ||
                    isSimpleEscalation ||
                    !(content || escalationMessage),
                  [styles.editMode]: isEdit,
                  [styles.translated]: !!translatedContent,
                })}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onBlur={() => { }}
              >
                {isHover ? (
                  <div className={styles.actionBar}>
                    <div className="flex items-center gap-2">
                      <Tooltip
                        delayDuration={100}
                        content={
                          translatedContent
                            ? "Show original"
                            : "Translate to English"
                        }
                      >
                        <span>
                          <PiTranslate />
                        </span>
                      </Tooltip>
                      <Switch
                        size="1"
                        disabled={isTranslating}
                        checked={!!translatedContent}
                        onCheckedChange={async (checked) => {
                          if (!checked) {
                            setTranslatedContent(null);
                            return;
                          }
                          setIsTranslating(true);
                          try {
                            const translated = await translateText(content);
                            setTranslatedContent(translated);
                            posthogTrackEvent({
                              event: SimulatorEvents.MESSAGE_SENT,
                              properties: {
                                messageLength: content.length,
                                messageType: "translated",
                                timestamp: new Date().toISOString(),
                              },
                            });
                          } catch (error) {
                            console.error("Translation failed:", error);

                            toast.error("Failed to translate message");
                          } finally {
                            setIsTranslating(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {(isHover || isEdit) &&
                  role === "assistant" &&
                  isLast &&
                  !editDisabled && (
                    <div
                      className={cx(styles.btnContainer, {
                        [styles.editablt]: isEdit,
                      })}
                    >
                      <Button
                        color="gray"
                        variant="ghost"
                        type="button"
                        className={cx(styles.btn)}
                        onClick={() => {
                          const newEditState = !isEdit;
                          setEdit(newEditState);
                          if (newEditState) {
                            posthogTrackEvent({
                              event: SimulatorEvents.SESSION_STARTED,
                              properties: {
                                sessionType: "enter_edit_mode",
                                timestamp: new Date().toISOString(),
                              },
                            });
                          }
                        }}
                      >
                        {isEdit ? "Cancel" : <Pencil1Icon />}
                      </Button>
                      {isEdit && (
                        <button
                          className={cx(styles.btn)}
                          onClick={() => {
                            handleEditMessage(value);
                            setEdit(false);
                            posthogTrackEvent({
                              event: SimulatorEvents.MESSAGE_SENT,
                              properties: {
                                messageLength: value.length,
                                messageType: "edited",
                                timestamp: new Date().toISOString(),
                              },
                            });
                          }}
                        >
                          Save
                        </button>
                      )}
                    </div>
                  )}
                {isLoading ? (
                  <Loading width={2.5} />
                ) : (
                  <>
                    {isEdit ? (
                      <TextField
                        value={content}
                        index={0}
                        handleBlur={() => { }}
                        handleFocus={() => { }}
                        setExternalValue={setValue}
                        preventSelectAll
                      />
                    ) : (
                      <div
                        className={cx(
                          styles.messageFormat,
                          isRtlContent && styles.rtl
                        )}
                        dangerouslySetInnerHTML={{
                          __html: convertMarkdownToHTML(displayContent),
                        }}
                      ></div>
                    )}
                  </>
                )}
              </div>
            )}
            <MessageSources
              isOpen={isOpen}
              setOpen={setOpen}
              sources={sources}
            />
          </>
        </Wrapper>
      )}

      {isEscalateWithMessage &&
        !isLoading &&
        tools?.map((tool, index) => {
          if (
            tool.state === "result" &&
            tool.toolName === "escalate_with_message"
          ) {
            return (
              <Wrapper
                key={tool.toolCallId || `tool-${index}`}
                {...props}
                isEscalation
                hideIcon
              >
                <ToolViewer tool={tool} isEscalation />
              </Wrapper>
            );
          }
          return null;
        })}
    </>
  );
};

const TextField = ({
  index,
  value,
  handleBlur,
  handleFocus,
  setExternalValue,
  disabled = false,
  preventSelectAll = false,
}: {
  index: number;
  value: string;
  setExternalValue?: (v: string) => void;
  handleBlur: (index: number, value: string) => void;
  handleFocus: (index: number) => void;
  disabled?: boolean;
  preventSelectAll?: boolean;
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [v, setValue] = useState(value);
  const [height, setHeight] = useState("unset");

  useEffect(() => {
    setValue(value);

    setTimeout(() => {
      if (textAreaRef.current) {
        setHeight(`${textAreaRef.current.scrollHeight}px`);
      }
    }, 300);
  }, [value]);

  return (
    <Textarea
      ref={textAreaRef}
      onChange={(e) => {
        const inputValue = e.target.value;
        setValue(inputValue);

        if (setExternalValue) {
          setExternalValue(inputValue);
        }
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
      }}
      onBlur={(e) => {
        handleBlur(index, e.target.value);
      }}
      onFocus={(e) => {
        handleFocus(index);
        if (!preventSelectAll) {
          e.target.select();
        }
      }}
      value={v}
      disabled={disabled}
      dir="auto"
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
      style={{
        resize: "none",
        width: "100%",
        overflow: "hidden",
        height: height,
        border: "0",
        overflowWrap: "break-word",
        wordBreak: "break-word",
      }}
    />
  );
};
